import { apiClient } from './client'
import type { Customer, TeamMember, UserProfile } from '@/types/user'

export interface AddUserPayload {
  firstname: string
  lastname: string
  email: string
  password: string
  /** Staff role; omitted for customers. */
  customer_type?: string
  assign_team_lead?: number | null
  subscription_type?: string
  custom_subscription?: number | string | null
  assign_qa?: number[] | null
}

export interface CsatRow {
  client_id: number
  client_fname: string
  client_lname: string
  card_tile: string
  rated_videos: number
  rating_comments?: string | null
  total_rating: number
  yt_rated_videos?: number
  yt_total_rating?: number
}

export interface ResponseRateRow {
  card_id: number
  card_title: string
  first_name: string
  last_name: string
  total_comments_count: number
  replied_count: number
  response_rate: number
  total_response_rated_cards: number
}

export interface RatingSearchCard {
  card_id: number
  card_title: string
  qa_assigned_to: number | null
  is_complete: 0 | 1
  repurpose_status: string | null
  is_repurpose_complete: 0 | 1
  repurpose_aspect: string | null
  card_rating: Array<{
    card_id: number
    rating: number
    card_type: 'done' | 'repurpose'
    assigned_qa_id: number
    card_rating_q_a?: { firstname: string; lastname: string; client_id: number } | null
  }>
}

export interface UpdatedRatingRow {
  rating_for: string
  created_at: string
  card_id: number
  created_by: number
  qa_id: number
  rating: number
  reason: string | null
  qaFirstname: string
  qaLastname: string
  card_title: string
}

export interface CustomerRatingRow {
  card_title: string
  rating: number
  customer_name: string
  qa_name: string
  comment: string | null
  created_date: string
}

const clean = <T extends object>(obj: T): Partial<T> =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== '')) as Partial<T>

export const usersApi = {
  team: () => apiClient.get<TeamMember[]>('/api/users').then((r) => (Array.isArray(r.data) ? r.data : [])),
  customers: () => apiClient.get<Customer[]>('/api/customers').then((r) => (Array.isArray(r.data) ? r.data : [])),
  add: (payload: AddUserPayload) =>
    apiClient.post<{ message: string; entity?: Partial<Customer & TeamMember> }>('/api/user', payload).then((r) => r.data),
  toggleActive: (clientId: number, active: boolean) =>
    apiClient
      .post<{ status: string }>(`/api/user/${clientId}/toggleUserSubscription`, {
        client_id: clientId,
        is_active_subscription: active ? 1 : 0,
      })
      .then((r) => r.data),
  renew: (clientId: number) => apiClient.post(`/api/user/${clientId}/renewSubscription`).then((r) => r.data),
  remove: (clientId: number) =>
    apiClient.delete<{ message: string; success?: boolean; error?: string }>(`/api/user/${clientId}`).then((r) => r.data),
  get: (clientId: number | string) => apiClient.get<UserProfile>(`/api/user/${clientId}`).then((r) => r.data),
  update: (clientId: number | string, payload: Record<string, unknown>) =>
    apiClient.put(`/api/user/${clientId}`, { ...payload, updatedFromProfile: true }).then((r) => r.data),
  updateOwnProfile: (payload: Record<string, unknown>) => apiClient.post('/api/user/profile', payload).then((r) => r.data),
  cardsCount: (customerIds: number[], customerType?: string) =>
    apiClient
      .post<{ totalAllowedVideoCount: number; oneMonthCreatedVideo: number }>(
        `/api/customers-cards-count${customerType ? `?customerType=${encodeURIComponent(customerType)}` : ''}`,
        customerIds,
      )
      .then((r) => r.data),
  csat: (clientId: number | string, params: { start_date: string; end_date: string; profileType: 'qa' | 'team-lead' }) =>
    apiClient
      .get<Record<string, CsatRow> | CsatRow[]>(`/api/user/${clientId}/average-rating`, { params })
      .then((r) => (Array.isArray(r.data) ? r.data : Object.values(r.data ?? {}))),
  responseRate: (
    clientId: number | string,
    params: { start_date: string; end_date: string; profileType: 'qa' | 'team-lead'; view_as?: string | number },
  ) =>
    apiClient
      .get<{ data: ResponseRateRow[]; count: number }>(`/api/user/${clientId}/responseRateV2`, {
        params: { type: 'comments', ...clean(params) },
      })
      .then((r) => ({ data: Array.isArray(r.data?.data) ? r.data.data : [], count: r.data?.count ?? 0 })),

  // ----- ratings -----
  searchCardsForRating: (title: string) =>
    apiClient.get<RatingSearchCard[]>('/api/getCardsByTitle', { params: { title } }).then((r) => (Array.isArray(r.data) ? r.data : [])),
  assignNewQa: (payload: { cardId: number; newQA: number; cardType: 'done' | 'repurpose'; rating: number; ratingReason: string }) =>
    apiClient.post<{ status: boolean; success?: string; error_message?: string }>('/api/assignNewQA', payload).then((r) => r.data),
  updatedRatings: (params: { startDate?: string; endDate?: string; limit: number; page: number; selectedRating?: string }) =>
    apiClient
      .get<{ updatedRatedCards: UpdatedRatingRow[]; totalRatedCards: number }>('/api/getUpdatedRatingCards', { params: clean(params) })
      .then((r) => r.data),
  customerRatings: (params: { limit: number; page: number; customerId?: string; qaId?: string; title?: string; commented?: boolean }) =>
    apiClient.get<{ data: CustomerRatingRow[]; total: number }>('/api/card-rated-comment', { params: clean(params) }).then((r) => r.data),
}
